import { useEffect, useRef, useState } from "react";
import { AbsoluteFill, continueRender, delayRender, useCurrentFrame, useVideoConfig } from "remotion";
import { selectCaptionRows } from "../lib/captionRows.ts";
import { tokenizeWords, type MeasureFn, type WordToken } from "../lib/captionWrap.ts";
import {
  animBlockStyle,
  clipLineToVisibleChars,
  DEFAULT_ANIM_MS,
  exitProgress,
  exitWindowMs,
  outBlockStyle,
  visibleLength,
} from "../lib/captionFx.ts";
import { activeWordIndex, wordProgress, wordWindows } from "../lib/karaoke.ts";
import {
  createCanvasMeasurer,
  createEstimateMeasurer,
  letterSpacingToPx,
} from "../lib/textMeasure.ts";
import { hasEmTag, splitDigits, splitEmphasis, stripEmTags } from "../lib/srt.ts";
import {
  LEGACY_CAPTION_LAYOUT,
  LEGACY_RENDER_BRAND,
  type RenderBrand,
  type RenderLayout,
  type SerializableCue,
} from "./types.ts";

interface Props {
  cues: SerializableCue[];
  brand?: RenderBrand;
  layout?: RenderLayout;
}

/** Legacy Quotologies defaults — single-sourced in types.ts so the Studio
 *  edit layer materializes the exact same values. */
const LEGACY_BRAND = LEGACY_RENDER_BRAND;
const LEGACY_LAYOUT = LEGACY_CAPTION_LAYOUT;

/** Hex + 0–1 opacity → rgba() string for the optional caption box. */
function rgba(hex: string, opacity: number): string {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16) || 0;
  const g = parseInt(m.slice(2, 4), 16) || 0;
  const b = parseInt(m.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${opacity})`;
}

/**
 * Strip <em>...</em> tags from a string. Kept for punch-style 2-line cues
 * WITHOUT spans: their founding render path is tag-free plain text, and any
 * stray tag must never paint literally. Cues that DO carry spans render
 * them instead (Phase 5) — see hasEm below.
 */
const stripEm = (text: string): string => stripEmTags(text);

/** True when a line carries at least one `<em>`/`<em c="…">` span. The gate
 *  that keeps span-free punch cues on the exact founding render path. */
const hasEm = (text: string): boolean => hasEmTag(text);

/**
 * Render a string with digit runs swapped to the punch font. Used for any
 * line where the base font is the caption body font. (Two-line cue's bottom
 * line is already punch-font end-to-end, so it skips this.)
 */
const renderWithDigitFont = (
  text: string,
  baseColor: string,
  keyPrefix: string,
  captionFont: string,
  punchFont: string,
): React.JSX.Element[] =>
  splitDigits(text).map((seg, i) => (
    <span
      key={`${keyPrefix}-${i}`}
      style={{
        fontFamily: seg.isDigit ? `${punchFont}, ${captionFont}, sans-serif` : `${captionFont}, sans-serif`,
        color: baseColor,
      }}
    >
      {seg.text}
    </span>
  ));


/**
 * Fonts-ready gate for measured wrap (wrapMode "auto" only — the founding
 * path never mounts this hook's effect, so its render timing is untouched).
 *
 * Forces the needed faces to LOAD (document.fonts.load — @font-face is lazy
 * until glyphs are used), holds the render with delayRender until they
 * resolve, and re-renders once so measurement runs against the real faces.
 * Preview (@remotion/player) and render (headless Chrome) both pass through
 * here, which is what makes their line breaks identical.
 */
function useMeasuredFontsReady(
  enabled: boolean,
  faces: Array<{ family: string; weight: number }>,
  sizePx: number,
): void {
  const [, bump] = useState(0);
  const doneRef = useRef<string | null>(null);
  const facesRef = useRef(faces);
  facesRef.current = faces;
  // Content key — the faces array is rebuilt every render; the key is what
  // actually gates re-loading, so the effect never loops on identity churn.
  const loadKey = enabled
    ? `${faces.map((f) => `${f.family}@${f.weight}`).join(",")}#${sizePx}`
    : "";
  useEffect(() => {
    if (!enabled || doneRef.current === loadKey) return;
    if (typeof document === "undefined" || !document.fonts) return;
    let live = true;
    let released = false;
    const handle = delayRender("caption measured-wrap fonts");
    const release = (): void => {
      if (!released) {
        released = true;
        continueRender(handle);
      }
    };
    Promise.all(
      facesRef.current.map((f) =>
        document.fonts.load(`${f.weight} ${sizePx}px "${f.family}"`).catch(() => []),
      ),
    ).then(() => {
      doneRef.current = loadKey;
      if (live) bump((t) => t + 1);
      release();
    });
    return () => {
      live = false;
      release();
    };
  }, [enabled, loadKey, sizePx]);
}

/**
 * Layout modes per cue:
 *
 *   • PUNCH style (default — cap.captionStyle absent or "punch"):
 *       – One-line cue: body font at fontSizePx; `<em>` spans in accent.
 *       – Two-line cue: line 1 in the body font (smaller), line 2 in the
 *         punch font at twoLine.bottomPx, all-accent (lands the punch).
 *         Spans render on BOTH lines when present (Phase 5); span-free
 *         cues take the exact founding render path.
 *
 *   • UNIFORM style (cap.captionStyle === "uniform"): every line at
 *     fontSizePx in the body font; color comes ONLY from `<em>` spans
 *     (base = captionColor, spans = captionAccent); explicit `\n` is
 *     respected and over-budget cues auto-wrap into balanced lines
 *     (src/lib/captionWrap.ts — the same pure module everywhere, so
 *     preview and render break identically by construction).
 *
 *   • LAST-LINE SIZE (cap.lastLineScale — UNIFORM style ONLY; the punch
 *     style, bigPunch and the auto line-punch alike, ignores it): the last
 *     row of a multi-row cue renders at round(fontSizePx × scale); when that
 *     row would overflow the frame the rows are re-chosen at the scaled size
 *     (rowsWithScaledLastLine). Absent/1 = the exact founding expressions,
 *     byte-identical.
 *
 * The translator decides 1 vs 2 lines by inserting a single `\n` at the
 * natural break point.
 */
export const CaptionLine = ({ cues, brand, layout }: Props): React.JSX.Element | null => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const ms = (frame / fps) * 1000;
  const b = brand ?? LEGACY_BRAND;
  const cap = layout?.caption ?? LEGACY_LAYOUT;
  const captionFont = b.captionFont;
  const punchFontStack = `${b.punchFont}, ${b.captionFont}, sans-serif`;

  // Text engine v2 (spec 2026-07-18): "auto" re-breaks lines from the real
  // available width via measured glyphs. The hook must run unconditionally
  // (Rules of Hooks) — it's inert when the mode is "authored".
  const wrapAuto = (cap.wrapMode ?? "authored") === "auto";
  useMeasuredFontsReady(
    wrapAuto,
    [
      { family: captionFont, weight: 700 },
      { family: b.punchFont, weight: 400 },
    ],
    cap.fontSizePx,
  );
  const spacingPx = letterSpacingToPx(cap.letterSpacing);
  const glyphRatio = b.glyphRatio ?? 0.6;
  const upper = cap.textTransform === "uppercase";
  /** Real canvas measurement when the face is loaded; estimate otherwise —
   *  render-side the fonts gate guarantees the real path.
   *
   *  Two width-parity rules (critic-caught, 2026-07-19):
   *  - textTransform "uppercase" paints wider glyphs than the raw text, so
   *    the measurer sees the uppercased string.
   *  - digit runs RENDER in the punch font (renderWithDigitFont) — so they
   *    must be MEASURED in it too, or number-bearing lines overflow. */
  const measFor = (family: string, weight: number, sizePx: number): MeasureFn => {
    const base =
      createCanvasMeasurer({
        fontFamily: `"${family}", sans-serif`,
        fontWeight: weight,
        fontSizePx: sizePx,
        letterSpacingPx: spacingPx,
      }) ?? createEstimateMeasurer(sizePx, glyphRatio, spacingPx);
    return upper ? (t) => base(t.toLocaleUpperCase()) : base;
  };

  // findLast: when two cues overlap (the lead-in shifts cue N+1 to start
  // slightly before cue N's true end), the LATER one wins so the reader sees
  // the new caption as the words begin being spoken.
  const activeIdx2 = cues.findLastIndex((c) => ms >= c.startMs && ms < c.endMs);
  const active = activeIdx2 >= 0 ? cues[activeIdx2] : undefined;
  if (!active) return null;
  // Exit animations play only INTO A PAUSE: when the next cue starts at (or
  // before — the 50ms lead-in overlap) this cue's end, the handoff must be
  // instant, or the outgoing caption dips mid-fade and the incoming one
  // snaps over it — a flicker on every sentence-adjacent cue (critic-caught).
  const nextCue = activeIdx2 >= 0 ? cues[activeIdx2 + 1] : undefined;
  const exitFree = !nextCue || nextCue.startMs > active.endMs;

  // Row selection lives in src/lib/captionRows.ts (pure, shared with the
  // measured fit gate) — every founding expression moved there verbatim.
  const { bigPunch, linePunch, uniformLines, topLines, bottomLines, lastScale, lastLinePx } =
    selectCaptionRows(active.text, cap, b, measFor);

  // ---------------------------------------------------------------------
  // Motion FX — karaoke word highlight (phase 2), entrance animation
  // (phase 2) and exit animation (phase 3). Everything below is inert
  // (empty styles, original render paths) when the fields are absent,
  // keeping founding renders byte-identical.
  // ---------------------------------------------------------------------
  const tMs = ms - active.startMs;
  const cueDurMs = active.endMs - active.startMs;
  const anim = cap.animation ?? null;
  // Phase 3: `in` is optional (an exit-only animation is legal). No `in` →
  // the entrance math is inert (animP pinned to 1).
  const inAnim = anim?.in ?? null;
  // The entrance may never eat more than 60% of the cue — otherwise a
  // durationMs ≥ cue length would keep typewriter truncated / stagger words
  // invisible for the WHOLE cue (critic-caught).
  const animMs = Math.min(
    anim?.durationMs ?? DEFAULT_ANIM_MS,
    cueDurMs > 0 ? cueDurMs * 0.6 : anim?.durationMs ?? DEFAULT_ANIM_MS,
  );
  const animP = inAnim && animMs > 0 ? Math.min(1, Math.max(0, tMs / animMs)) : 1;
  const typing = inAnim === "typewriter" && animP < 1;
  const stagger = inAnim === "word-stagger" && animP < 1;
  // Exit window: the cue's tail, clamped to 40% of the cue so it can never
  // reach the entrance's 60% head (exitWindowMs). outP runs 0 → 1 across
  // the window. Suppressed entirely when another cue butts in (exitFree).
  const outAnim = exitFree ? anim?.out ?? null : null;
  const outMs = outAnim ? exitWindowMs(cueDurMs, anim?.outDurationMs) : 0;
  const outP = outAnim ? exitProgress(tMs, cueDurMs, outMs) : 0;

  // Displayed lines in reading order (before any typewriter clipping) —
  // the shared basis for karaoke windows and stagger indices.
  const displayedLines = bigPunch ? [...topLines, ...bottomLines] : uniformLines;

  // Typewriter: clip lines to the revealed char budget; karaoke pauses
  // while typing (indices against clipped words would drift).
  let charBudget = Infinity;
  if (typing) {
    const total = displayedLines.reduce((n, l) => n + visibleLength(l), 0);
    charBudget = Math.floor(animP * total);
  }
  const clipLines = (lines: string[], consumedBefore: number): string[] =>
    !typing
      ? lines
      : lines.map((l) => {
          const budget = charBudget - consumedBefore;
          consumedBefore += visibleLength(l);
          const clipped = clipLineToVisibleChars(l, budget);
          // Zero-width space keeps an empty not-yet-typed line's LINE BOX —
          // without it the block height grows per revealed line and the
          // already-typed lines jump vertically (critic-caught).
          return clipped === "" ? "​" : clipped;
        });
  const topConsumed = 0;
  const bottomConsumed = topLines.reduce((n, l) => n + visibleLength(l), 0);
  const fxTopLines = bigPunch ? clipLines(topLines, topConsumed) : [];
  const fxBottomLines = bigPunch ? clipLines(bottomLines, bottomConsumed) : [];
  const fxUniformLines = bigPunch ? [] : clipLines(uniformLines, 0);

  const karaokeOn = !!cap.karaoke && cueDurMs > 0 && !typing;
  // One word model for the WHOLE cue: per-line tokens + global windows.
  const lineTokens: WordToken[][] = karaokeOn || stagger ? displayedLines.map(tokenizeWords) : [];
  const lineStartIdx: number[] = [];
  {
    let acc = 0;
    for (const toks of lineTokens) {
      lineStartIdx.push(acc);
      acc += toks.length;
    }
  }
  const allWords = lineTokens.flat();
  const windows = karaokeOn ? wordWindows(allWords.map((w) => w.plain), cueDurMs) : [];
  const activeIdx = karaokeOn ? activeWordIndex(windows, tMs) : -1;
  /** Explicit color wins; the default must be DISTINCT from each line's
   *  base — accent on body lines, but body color on the all-accent punch
   *  line, where accent-on-accent would be invisible (critic-caught). */
  const karaokeColorFor = (base: "body" | "punch"): string =>
    cap.karaoke?.color ?? (base === "punch" ? b.captionColor : b.captionAccent);
  const staggerReveal = stagger ? animP * allWords.length : Infinity;
  /** True when line content must render word-by-word (karaoke / stagger). */
  const wordFx = karaokeOn || stagger;

  /** Word-by-word line renderer — mirrors the segment renderers' color and
   *  digit-font rules exactly, plus the karaoke highlight / stagger fade. */
  const renderFxLine = (
    displayLineIdx: number,
    base: "body" | "punch",
    keyPrefix: string,
  ): React.JSX.Element[] => {
    const toks = lineTokens[displayLineIdx] ?? [];
    const start = lineStartIdx[displayLineIdx] ?? 0;
    const out: React.JSX.Element[] = [];
    toks.forEach((w, i) => {
      const gIdx = start + i;
      if (i > 0) out.push(<span key={`${keyPrefix}-sp-${i}`}>{" "}</span>);
      const isActive = karaokeOn && gIdx === activeIdx;
      const popScale =
        isActive && cap.karaoke!.mode === "pop"
          ? 1 + 0.16 * (1 - Math.abs(2 * wordProgress(windows, gIdx, tMs) - 1))
          : 1;
      const wordOpacity = stagger ? Math.min(1, Math.max(0, staggerReveal - gIdx)) : 1;
      out.push(
        <span
          key={`${keyPrefix}-w-${i}`}
          style={{
            ...(popScale !== 1 || wordOpacity !== 1
              ? {
                  display: "inline-block",
                  ...(popScale !== 1 ? { transform: `scale(${popScale.toFixed(4)})` } : {}),
                  ...(wordOpacity !== 1 ? { opacity: wordOpacity } : {}),
                }
              : {}),
          }}
        >
          {w.frags.flatMap((frag, fi) => {
            // Explicit per-span color wins everywhere except while this word
            // is the active karaoke word (the highlight owns the color then).
            const fragColor = isActive
              ? karaokeColorFor(base)
              : frag.color
                ?? (base === "punch"
                  ? b.captionAccent
                  : frag.em
                    ? b.captionAccent
                    : b.captionColor);
            if (base === "punch") {
              return [
                <span key={`${keyPrefix}-w-${i}-f-${fi}`} style={{ color: fragColor }}>
                  {frag.text}
                </span>,
              ];
            }
            return renderWithDigitFont(frag.text, fragColor, `${keyPrefix}-w-${i}-f-${fi}`, captionFont, b.punchFont);
          })}
        </span>,
      );
    });
    return out;
  };

  const boxStyle = cap.box
    ? {
        backgroundColor: rgba(cap.box.color, cap.box.opacity),
        borderRadius: cap.box.radiusPx,
        padding: `${cap.box.padYPx}px ${cap.box.padXPx}px`,
      }
    : {};

  // Styling v2 extras — every one default-absent = founding pixels.
  const align = cap.align ?? "center";
  const strokeStyle = cap.stroke
    ? {
        WebkitTextStroke: `${cap.stroke.widthPx}px ${cap.stroke.color}`,
        paintOrder: "stroke fill" as const,
      }
    : {};
  // align start/end: the block itself shifts to the frame edge (alignSelf on
  // the outer flex — direction-aware, since that flex container has no dir)
  // and lines align inside a still-shrink-wrapped block, so a cap.box keeps
  // hugging the text and the overlay's hit-rect stays honest.
  const dirRtl = (b.direction ?? "rtl") === "rtl";
  const physicalSide =
    align === "center" ? null : (align === "start") === dirRtl ? ("flex-end" as const) : ("flex-start" as const);
  // Entrance animation (fade/slide-up/pop are block-level; typewriter and
  // word-stagger return {} here — they act on content). Opacity multiplies
  // with the static caption opacity. The exit style joins at the cue tail;
  // its window can never overlap the entrance (40% vs 60% clamps), so when
  // both request a transform the exit's is the live one.
  const animStyle = inAnim ? animBlockStyle(inAnim, animP, cap.fontSizePx) : {};
  const outStyle = outAnim ? outBlockStyle(outAnim, outP, cap.fontSizePx) : {};
  const staticOpacity = cap.opacity !== undefined && cap.opacity !== 1 ? cap.opacity : 1;
  const mergedOpacity = staticOpacity * (animStyle.opacity ?? 1) * (outStyle.opacity ?? 1);
  const fxTransform = outStyle.transform ?? animStyle.transform;
  const blockExtras = {
    ...(physicalSide
      ? { alignSelf: physicalSide, alignItems: physicalSide, textAlign: align }
      : {}),
    ...(cap.textTransform === "uppercase" ? { textTransform: "uppercase" as const } : {}),
    ...(mergedOpacity !== 1 ? { opacity: mergedOpacity } : {}),
    ...(fxTransform ? { transform: fxTransform } : {}),
  };

  // linePunch ONE-line cue: first half white, latter half accent (the
  // operator's one-line rule, applied by position — <em> already stripped).
  const renderSecondHalfAccent = (plain: string, keyPrefix: string): React.JSX.Element[] => {
    const ws = plain.split(/\s+/).filter(Boolean);
    if (ws.length < 2) return renderWithDigitFont(plain, b.captionColor, keyPrefix, captionFont, b.punchFont);
    const mid = Math.floor(ws.length / 2);
    return [
      ...renderWithDigitFont(ws.slice(0, mid).join(" ") + " ", b.captionColor, `${keyPrefix}-a`, captionFont, b.punchFont),
      ...renderWithDigitFont(ws.slice(mid).join(" "), b.captionAccent, `${keyPrefix}-b`, captionFont, b.punchFont),
    ];
  };

  return (
    <AbsoluteFill
      style={{
        justifyContent: cap.justify,
        alignItems: "center",
        paddingBottom: cap.bottomPx,
        paddingLeft: cap.sidePaddingPx,
        paddingRight: cap.sidePaddingPx,
        pointerEvents: "none",
      }}
    >
      <div
        dir={b.direction ?? "rtl"}
        // Hit-test seam for the Studio canvas overlay (inert in renders —
        // data attributes never paint). The overlay measures this node's
        // rect to draw selection chrome exactly around the visible block.
        data-studio-el="caption"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: "100%",
          ...boxStyle,
          ...blockExtras,
        }}
      >
        {bigPunch ? (
          <>
            {fxTopLines.map((topLine, ti) => (
              <div
                key={`top-${ti}`}
                style={{
                  fontWeight: 700,
                  fontSize: cap.twoLine!.topPx,
                  // Founding geometry when unset (1.15 / 1.0 / 28px below) —
                  // the overrides exist because the inter-line gap here is
                  // font-box + margin driven, NOT cap.lineHeight, and the
                  // operator needs to tighten it ("it needs to be tight").
                  lineHeight: cap.twoLine!.lineHeight ?? 1.15,
                  textShadow: cap.textShadow,
                  whiteSpace: "nowrap",
                  letterSpacing: cap.letterSpacing,
                  ...strokeStyle,
                }}
              >
                {wordFx
                  ? renderFxLine(ti, "body", `top${ti}`)
                  : hasEm(topLine)
                  ? splitEmphasis(topLine).flatMap((seg, i) =>
                      renderWithDigitFont(
                        seg.text,
                        seg.color ?? (seg.emphasized ? b.captionAccent : b.captionColor),
                        `top${ti}-em-${i}`,
                        captionFont,
                        b.punchFont,
                      ),
                    )
                  : renderWithDigitFont(stripEm(topLine), b.captionColor, `top${ti}`, captionFont, b.punchFont)}
              </div>
            ))}
            {fxBottomLines.map((bottomLine, bi) => (
              <div
                key={`bottom-${bi}`}
                style={{
                  fontFamily: punchFontStack,
                  fontWeight: 400,
                  fontSize: cap.twoLine!.bottomPx,
                  lineHeight: cap.twoLine!.lineHeight ?? 1.0,
                  color: b.captionAccent,
                  textShadow: cap.textShadow,
                  whiteSpace: "nowrap",
                  letterSpacing: cap.letterSpacing,
                  // gapPx may be negative — lines can hug or overlap. Only
                  // the FIRST punch line carries the gap; wrapped
                  // continuation lines stack on their own line height.
                  ...(bi === 0 ? { marginTop: cap.twoLine!.gapPx ?? 28 } : {}),
                  ...strokeStyle,
                }}
              >
                {wordFx
                  ? renderFxLine(fxTopLines.length + bi, "punch", `bottom${bi}`)
                  : hasEm(bottomLine)
                  // The punch line is all-accent by design, so a plain `<em>`
                  // span is color-invisible here — but an EXPLICIT color still
                  // wins (the operator coloring a word on the punch line), and
                  // every span must render as TEXT, never a literal tag.
                  ? splitEmphasis(bottomLine).map((seg, i) => (
                      <span key={`bottom${bi}-em-${i}`} style={{ color: seg.color ?? b.captionAccent }}>
                        {seg.text}
                      </span>
                    ))
                  : stripEm(bottomLine)}
              </div>
            ))}
          </>
        ) : (
          fxUniformLines.map((line, li) => {
            // linePunch coloring by LINE: two lines → line 1 white / line 2
            // accent; one line → second half accent. Ignores <em> entirely.
            // accentMode "line-punch" (operator policy) applies the same
            // positional coloring in UNIFORM style: last line all-accent on
            // multi-line cues, second half on one-liners — every caption,
            // no per-cue spans needed.
            const accentPolicy = cap.accentMode === "line-punch";
            const twoLinePunch =
              (linePunch && fxUniformLines.length === 2) || (accentPolicy && fxUniformLines.length >= 2);
            const oneLinePunch =
              (linePunch || accentPolicy) && fxUniformLines.length === 1;
            const punchLineIdx = fxUniformLines.length - 1;
            return (
            <div
              key={`uni-${li}`}
              style={{
                fontWeight: 700,
                // The last row of a multi-row cue takes the scaled size; at
                // lastScale 1 every row is exactly cap.fontSizePx.
                fontSize: lastScale !== 1 && fxUniformLines.length >= 2 && li === punchLineIdx
                  ? lastLinePx
                  : cap.fontSizePx,
                lineHeight: cap.lineHeight,
                textShadow: cap.textShadow,
                whiteSpace: "nowrap",
                letterSpacing: cap.letterSpacing,
                ...strokeStyle,
              }}
            >
              {wordFx
                ? renderFxLine(li, twoLinePunch && li === punchLineIdx ? "punch" : "body", `uni${li}`)
                : twoLinePunch
                ? renderWithDigitFont(
                    stripEm(line),
                    li === punchLineIdx ? b.captionAccent : b.captionColor,
                    `uni${li}`,
                    captionFont,
                    b.punchFont,
                  )
                : oneLinePunch
                ? renderSecondHalfAccent(stripEm(line), `uni${li}`)
                : splitEmphasis(line).flatMap((seg, i) =>
                    renderWithDigitFont(
                      seg.text,
                      seg.color ?? (seg.emphasized ? b.captionAccent : b.captionColor),
                      `em${li}-${i}`,
                      captionFont,
                      b.punchFont,
                    ),
                  )}
            </div>
            );
          })
        )}
      </div>
    </AbsoluteFill>
  );
};
